import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { projectKeys, scriptKeys } from "queries";
import { catalogKeys, sessionKeys } from "queries/profile.keys";
import { versionKeys } from "queries/version.keys";
import { useAxios } from "hooks/useAxios";
import { useAnalytics } from "hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "analytics";
import { toastMsg } from "config";
import { setProjectData, setSelectedDocumentId } from "store/reducers/project";
import type { ProjectDataType } from "store/types/project";
import { handleToast, handleToastWithAction, ToastActionButton } from "utils/toasts";
import { finalDraftTagsKeptMessage, type FinalDraftTagCounts } from "services/finalDraftTags";
import {
  awaitImportJob,
  describeCreateProjectError,
  executeCreateProject,
  isAsyncImportEnabled,
  listImportJobs,
  type CreateProjectExecuteInput,
  type CreateProjectStage,
  type ImportJobRow,
} from "services/createProjectTask";
import type {
  BackgroundTasksContextValue,
  CreateProjectTask,
  StartCreateProjectOptions,
} from "./backgroundTasks.types";

export const BackgroundTasksContext = createContext<BackgroundTasksContextValue | null>(null);

const progressToastId = (taskId: string) => `create-project-progress-${taskId}`;
const resultToastId = (taskId: string) => `create-project-result-${taskId}`;
// A dismissed toast keeps its id until its exit animation ends, and react-toastify drops a new
// toast with that id. So failures never reuse resultToastId (a fast retry would lose the success
// toast), and each failure gets its own id (a retry that fails again would lose its failure toast).
const failedToastId = (taskId: string, attempt: number) =>
  `create-project-failed-${taskId}-${attempt}`;

const stageCopy = (task: CreateProjectTask): string => {
  const label = task.mode === "version" ? "version" : "project";
  switch (task.stage) {
    case "uploading": {
      const pct =
        typeof task.uploadProgress === "number"
          ? ` (${Math.round(task.uploadProgress * 100)}%)`
          : "";
      return `Uploading "${task.projectName}"${pct}… you can keep working.`;
    }
    case "parsing":
      return `Parsing "${task.projectName}"… you can keep working.`;
    case "finalizing":
      return `Finalizing ${label} "${task.projectName}"…`;
    case "creating":
    default:
      return `Creating ${label} "${task.projectName}"… you can keep working.`;
  }
};

export const BackgroundTasksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const [tasks, setTasks] = useState<CreateProjectTask[]>([]);
  const tasksRef = useRef<CreateProjectTask[]>([]);
  tasksRef.current = tasks;

  // Surfaces (wizard/sheet) watching a task get the completion callback; once
  // released, completion falls back to toasts. Refs, not state: no re-render needed.
  const watchersRef = useRef<Map<string, StartCreateProjectOptions>>(new Map());
  // Forward reference: runTask's error toast offers Retry before the retry
  // callback is declared.
  const retryRef = useRef<(taskId: string) => void>();
  // The failure toast on screen for each task, so a retry from the pill can clear it.
  const failedToastIdsRef = useRef<Map<string, string>>(new Map());
  const failureCountRef = useRef(0);

  const updateTask = useCallback((taskId: string, patch: Partial<CreateProjectTask>) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
  }, []);

  const getTask = useCallback(
    (taskId: string) => tasksRef.current.find((t) => t.id === taskId),
    []
  );

  const showProgressToast = useCallback((task: CreateProjectTask) => {
    toast.dismiss(progressToastId(task.id));
    handleToastWithAction(stageCopy(task), null, "info", "bottom-left", progressToastId(task.id));
  }, []);

  const refreshProgressToastIfShown = useCallback(
    (taskId: string) => {
      const task = tasksRef.current.find((t) => t.id === taskId);
      if (!task || task.status !== "running") return;
      // Only surface the toast once the launching surface stopped watching.
      if (watchersRef.current.has(taskId)) return;
      showProgressToast(task);
    },
    [showProgressToast]
  );

  const openProject = useCallback(
    (task: CreateProjectTask, project: ProjectDataType) => {
      dispatch(setProjectData(project));
      if (task.mode === "version" && project.active_document_id) {
        dispatch(
          setSelectedDocumentId({
            projectId: String(project.project_id),
            documentId: project.active_document_id,
          })
        );
      }
      const workspaceId = task.workspaceId;
      const projectId =
        task.mode === "version"
          ? ((task.input?.existingProject as ProjectDataType | undefined)?.project_id ??
            project.project_id)
          : project.project_id;
      if (workspaceId && projectId) {
        navigate(`/workspaces/${workspaceId}/projects/${projectId}/script/breakdown`);
      }
    },
    [dispatch, navigate]
  );

  const applySuccessSideEffects = useCallback(
    (task: CreateProjectTask, data: ProjectDataType) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.list() });

      const project = (data as { data?: ProjectDataType })?.data ?? data;
      const existingProjectId =
        task.mode === "version"
          ? (task.input?.existingProject as ProjectDataType | undefined)?.project_id
          : undefined;

      // Both modes need the detail and scene caches refreshed. Skipping this for versions left
      // the open editor showing the previous screenplay after a successful upload.
      const detailProjectId = project?.project_id ?? existingProjectId;
      if (detailProjectId) {
        // Seed the cache only for a brand-new project, where there is nothing to overwrite.
        // The create response is the raw project entity, far thinner than what GET /projects/:id
        // returns, so writing it over an existing detail entry would drop ai_credits, seating
        // and permissions until the refetch lands. Versions invalidate and refetch instead.
        if (task.mode === "project" && project?.project_id) {
          queryClient.setQueryData(projectKeys.detail(project.project_id), project);
        }
        queryClient.invalidateQueries({ queryKey: projectKeys.detail(detailProjectId) });
        const documentId =
          project?.active_document_id ??
          (project as { documents?: Array<{ document_id?: string }> })?.documents?.[0]?.document_id;
        if (documentId) {
          queryClient.invalidateQueries({
            queryKey: scriptKeys.scenes(String(detailProjectId), documentId),
          });
        }
      }

      if (existingProjectId) {
        queryClient.invalidateQueries({ queryKey: versionKeys.list(String(existingProjectId)) });
      }

      // Redux "current project" sync is only safe when the user is still looking
      // at (or being navigated to) that project; a background completion while
      // they work in another project must not clobber it.
      const watcher = watchersRef.current.get(task.id);
      const stillOnProject =
        typeof window !== "undefined" &&
        Boolean(existingProjectId) &&
        window.location.pathname.includes(String(existingProjectId));
      if (task.mode === "version" && project?.project_id && (watcher || stillOnProject)) {
        dispatch(setProjectData(project));
        if (project.active_document_id) {
          dispatch(
            setSelectedDocumentId({
              projectId: String(project.project_id),
              documentId: project.active_document_id,
            })
          );
        }
      }
      if (task.mode === "project" && project?.project_id && watcher) {
        dispatch(setProjectData(project));
      }

      if (task.mode === "project" && project?.project_id) {
        const isTrial =
          project.is_trial ||
          (project as { trial_expiry_date?: string }).trial_expiry_date ||
          (project as { purchase_type?: string }).purchase_type === "trial";
        trackEvent(ANALYTICS_EVENTS.PROJECT_CREATED, {
          project_id: project.project_id,
          is_trial: isTrial,
          production_type: project.production_type,
          create_source: task.input?.source ?? "import",
        });
      }

      // Kept distinct from PROJECT_CREATED so version uploads do not inflate the
      // project-creation funnel that activation metrics read from.
      if (task.mode === "version" && existingProjectId) {
        trackEvent(ANALYTICS_EVENTS.PROJECT_VERSION_CREATED, {
          project_id: existingProjectId,
          document_id: project?.active_document_id ?? null,
          create_source: task.input?.source ?? "import",
        });
      }

      queryClient.invalidateQueries({ queryKey: sessionKeys.all });
      queryClient.invalidateQueries({ queryKey: catalogKeys.all });
      queryClient.invalidateQueries({ queryKey: [...projectKeys.all, "quota"] as const });

      return project;
    },
    [dispatch, queryClient, trackEvent]
  );

  const finishTaskSuccess = useCallback(
    (taskId: string, created: ProjectDataType, finalDraftTags: FinalDraftTagCounts | null) => {
      const task = getTask(taskId);
      if (!task) return;
      const project = applySuccessSideEffects(task, created);
      updateTask(taskId, { status: "done", project });
      toast.dismiss(progressToastId(taskId));

      const watcher = watchersRef.current.get(taskId);
      watchersRef.current.delete(taskId);
      const successMsg =
        task.mode === "version" ? "Version created successfully" : "Project created successfully";
      const keptTagsMsg = finalDraftTagsKeptMessage(finalDraftTags);
      if (watcher) {
        handleToast(keptTagsMsg ? `${successMsg}. ${keptTagsMsg}` : successMsg, "success");
        if (project?.project_id) {
          watcher.onSuccess?.(project);
        }
      } else {
        handleToastWithAction(
          keptTagsMsg
            ? `${successMsg}: "${task.projectName}". ${keptTagsMsg}`
            : `${successMsg}: "${task.projectName}"`,
          <ToastActionButton
            onClick={() => {
              toast.dismiss(resultToastId(taskId));
              const current = getTask(taskId);
              if (current && project) {
                openProject(current, project);
              }
            }}
          >
            Open
          </ToastActionButton>,
          "success",
          "bottom-left",
          resultToastId(taskId)
        );
      }
    },
    [applySuccessSideEffects, getTask, openProject, updateTask]
  );

  const finishTaskError = useCallback(
    (taskId: string, err: unknown) => {
      const task = getTask(taskId);
      const info = describeCreateProjectError(err);
      const createSource = task?.input?.source ?? (task?.input?.file ? "import" : "blank");
      const mode = task?.mode ?? "project";

      if (info.canceled) {
        trackEvent(ANALYTICS_EVENTS.PROJECT_CREATE_ABORTED, {
          create_source: createSource,
          mode,
          abort_reason: "canceled",
        });
        toast.dismiss(progressToastId(taskId));
        watchersRef.current.delete(taskId);
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
        return;
      }

      trackEvent(ANALYTICS_EVENTS.PROJECT_CREATE_FAILED, {
        http_status: info.httpStatus,
        api_error_code: info.apiErrorCode,
        create_source: createSource,
        mode,
        axios_code: info.axiosCode,
      });

      let errorMessage = info.message || toastMsg.upload.failed;
      if (info.httpStatus === 403) {
        if (info.apiErrorCode === "PROJECT_LIMIT_REACHED") {
          errorMessage =
            "You have reached your project limit. Please purchase additional licenses to create more projects.";
        } else if (info.message?.includes("one-time purchase licenses")) {
          errorMessage =
            "You have exhausted all your one-time purchase licenses. Please purchase more licenses to continue.";
        } else {
          errorMessage = info.message || "Failed to create project";
        }
      }

      watchersRef.current.delete(taskId);
      updateTask(taskId, { status: "error", errorMessage });
      toast.dismiss(progressToastId(taskId));
      const canRetry = Boolean(task?.input);
      failureCountRef.current += 1;
      const toastId = failedToastId(taskId, failureCountRef.current);
      failedToastIdsRef.current.set(taskId, toastId);
      handleToastWithAction(
        `Creating "${task?.projectName ?? "project"}" failed: ${errorMessage}`,
        canRetry ? (
          <ToastActionButton
            onClick={() => {
              toast.dismiss(toastId);
              retryRef.current?.(taskId);
            }}
          >
            Retry
          </ToastActionButton>
        ) : null,
        "error",
        "bottom-left",
        toastId
      );
    },
    [getTask, trackEvent, updateTask]
  );

  const runTask = useCallback(
    async (taskId: string, input: CreateProjectExecuteInput) => {
      try {
        let finalDraftTags: FinalDraftTagCounts | null = null;
        const created = await executeCreateProject({ axios, queryClient }, input, {
          onStage: (stage: CreateProjectStage) => {
            updateTask(taskId, { stage });
            refreshProgressToastIfShown(taskId);
          },
          onUploadProgress: (fraction) => {
            updateTask(taskId, { uploadProgress: fraction });
          },
          onJobEnqueued: (jobId) => {
            updateTask(taskId, { importJobId: jobId, serverBacked: true });
          },
          onFinalDraftTags: (counts) => {
            finalDraftTags = counts;
          },
        });
        finishTaskSuccess(taskId, created, finalDraftTags);
      } catch (err: unknown) {
        finishTaskError(taskId, err);
      }
    },
    [
      axios,
      finishTaskError,
      finishTaskSuccess,
      queryClient,
      refreshProgressToastIfShown,
      updateTask,
    ]
  );

  const hasActiveCreateTask = useCallback((workspaceId?: string) => {
    return tasksRef.current.some(
      (t) =>
        t.status === "running" &&
        t.mode === "project" &&
        (workspaceId === undefined || t.workspaceId === workspaceId)
    );
  }, []);

  /** Versions are scoped to one project, so two different projects can import at once. */
  const hasActiveVersionTask = useCallback((projectId?: string) => {
    if (!projectId) return false;
    return tasksRef.current.some(
      (t) =>
        t.status === "running" &&
        t.mode === "version" &&
        (t.input?.existingProject as { project_id?: string } | undefined)?.project_id === projectId
    );
  }, []);

  const startCreateProject = useCallback(
    (input: CreateProjectExecuteInput, options?: StartCreateProjectOptions): string | null => {
      const isVersion = input.mode === "version";
      const targetProjectId = (input.existingProject as { project_id?: string } | undefined)
        ?.project_id;

      // Project mode: one active creation per workspace until the backend enforces idempotency,
      // which prevents accidental duplicate projects. Version mode locks on the target project
      // only, so an unrelated import elsewhere in the workspace cannot block it.
      if (
        isVersion ? hasActiveVersionTask(targetProjectId) : hasActiveCreateTask(input.workspaceId)
      ) {
        handleToast(
          isVersion
            ? "A new version is already being created for this project. Please wait for it to finish."
            : "A project is already being created in this workspace. Please wait for it to finish.",
          "warning"
        );
        return null;
      }

      const id = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      const task: CreateProjectTask = {
        id,
        projectName: input.formData?.name || input.file?.name || "Untitled project",
        fileName: input.file?.name,
        fileSize: input.file?.size,
        workspaceId: input.workspaceId,
        mode: input.mode === "version" ? "version" : "project",
        status: "running",
        stage: input.file ? "uploading" : "creating",
        startedAt: Date.now(),
        input,
      };
      if (options) {
        watchersRef.current.set(id, options);
      }
      setTasks((prev) => [...prev, task]);
      void runTask(id, input);
      return id;
    },
    [hasActiveCreateTask, hasActiveVersionTask, runTask]
  );

  const releaseCreateProjectTask = useCallback(
    (taskId: string) => {
      if (!watchersRef.current.has(taskId)) return;
      watchersRef.current.delete(taskId);
      // The surface is gone; from here on the toast is the progress UI.
      refreshProgressToastIfShown(taskId);
    },
    [refreshProgressToastIfShown]
  );

  const dismissCreateProjectTask = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId || t.status === "running"));
  }, []);

  const retryCreateProjectTask = useCallback(
    (taskId: string) => {
      const task = tasksRef.current.find((t) => t.id === taskId);
      if (!task || task.status !== "error" || !task.input) return;
      // Retry can come from the pill, not only the toast, so clear the failed toast here too.
      const failedId = failedToastIdsRef.current.get(taskId);
      if (failedId) {
        toast.dismiss(failedId);
        failedToastIdsRef.current.delete(taskId);
      }
      updateTask(taskId, { status: "running", stage: task.input.file ? "uploading" : "creating" });
      refreshProgressToastIfShown(taskId);
      void runTask(taskId, task.input);
    },
    [refreshProgressToastIfShown, runTask, updateTask]
  );
  retryRef.current = retryCreateProjectTask;

  // Reload rehydration (async import path): pick up server-side jobs still in
  // flight and resume polling them, so a refresh mid-import keeps its progress
  // surfaces instead of losing the task.
  const rehydratedRef = useRef(false);
  useEffect(() => {
    if (rehydratedRef.current || !isAsyncImportEnabled()) return;
    rehydratedRef.current = true;

    void (async () => {
      let jobs: ImportJobRow[] = [];
      try {
        jobs = await listImportJobs({ axios, queryClient });
      } catch {
        return; // Endpoint unavailable (backend not deployed yet); stay quiet.
      }
      const active = jobs.filter(
        (job) => job.status === "queued" || job.status === "parsing" || job.status === "creating"
      );
      for (const job of active) {
        if (tasksRef.current.some((t) => t.importJobId === job.job_id)) continue;
        const id = `import-${job.job_id}`;
        const task: CreateProjectTask = {
          id,
          projectName: job.settings?.name || job.file_name || "Project import",
          fileName: job.file_name ?? undefined,
          workspaceId: job.workspace_id != null ? String(job.workspace_id) : undefined,
          mode: "project",
          status: "running",
          stage: job.status === "parsing" ? "parsing" : "creating",
          startedAt: Date.now(),
          importJobId: job.job_id,
          serverBacked: true,
        };
        setTasks((prev) => [...prev, task]);
        refreshProgressToastIfShown(id);
        void (async () => {
          try {
            let finalDraftTags: FinalDraftTagCounts | null = null;
            const created = await awaitImportJob({ axios, queryClient }, job.job_id, {
              onStage: (stage) => {
                updateTask(id, { stage });
                refreshProgressToastIfShown(id);
              },
              onFinalDraftTags: (counts) => {
                finalDraftTags = counts;
              },
            });
            finishTaskSuccess(id, created, finalDraftTags);
          } catch (err: unknown) {
            finishTaskError(id, err);
          }
        })();
      }
    })();
  }, [
    axios,
    finishTaskError,
    finishTaskSuccess,
    queryClient,
    refreshProgressToastIfShown,
    updateTask,
  ]);

  // A running task that is not yet server-backed dies with the tab; warn.
  const hasFragileRunning = tasks.some((t) => t.status === "running" && !t.serverBacked);
  useEffect(() => {
    if (!hasFragileRunning) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasFragileRunning]);

  const value = useMemo<BackgroundTasksContextValue>(
    () => ({
      createProjectTasks: tasks,
      startCreateProject,
      releaseCreateProjectTask,
      dismissCreateProjectTask,
      retryCreateProjectTask,
      hasActiveCreateTask,
    }),
    [
      dismissCreateProjectTask,
      hasActiveCreateTask,
      releaseCreateProjectTask,
      retryCreateProjectTask,
      startCreateProject,
      tasks,
    ]
  );

  return (
    <BackgroundTasksContext.Provider value={value}>{children}</BackgroundTasksContext.Provider>
  );
};
